import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Delete, DownloadIcon, Edit, ViewIcon } from '../../../assets/svg';
import { MY_DOCUMENTS, QUERY_KEYS } from '../../../constants';
import { getContentTypeByExtension, getFileExtension, popup } from '../../../utils';
import { DeleteConfirmationModal } from '../../shared';
import { EditFileNameModal } from '../../shared/Modal/EditFileNameModal';
import {
    deleteMyDocumentsAPI,
    updateMyDocumentsAPI,
} from '../../../utils/apiServices/myDocumentsAPI';
import useFileHandler from '../../../hooks/useFileHandler';

const MyDocumentActions = ({ data }) => {
    const { id, file_name, s3_key } = data;
    const queryClient = useQueryClient();
    const extension = getFileExtension(s3_key);
    const fileType = getContentTypeByExtension(extension);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const { viewHandler, downloadHandler } = useFileHandler({
        fileType,
        file_name,
        s3_key,
        extension,
    });

    const editHandler = () => {
        setShowEditModal(true);
    };

    const deleteMutation = useMutation({
        mutationFn: async (selectedItemId) =>
            await deleteMyDocumentsAPI({
                data: { ids: [selectedItemId], permanently: false },
            }),
        onSuccess: ({ message }) => {
            popup('success', message);
            setIsDeleteModalOpen(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MY_DOCUMENTS],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MY_DELETED_DOCUMENTS],
                exact: false,
            });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const deleteHandler = ({ selectedItemId }) => {
        deleteMutation.mutate(selectedItemId);
    };

    const renameMutation = useMutation({
        mutationFn: async (values) =>
            await updateMyDocumentsAPI({
                data: [{ id, file_name: values.fileName }],
            }),
        onSuccess: ({ message }) => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MY_DOCUMENTS],
                exact: false,
            });
            popup('success', message);
            setShowEditModal(false);
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const editModalOnSubmit = (values) => {
        renameMutation.mutate(values);
    };

    return (
        <div>
            <div className='d-flex gap-2'>
                <span title={MY_DOCUMENTS.VIEW} id='action-btn' onClick={viewHandler}>
                    <ViewIcon />
                </span>
                <span title={MY_DOCUMENTS.DOWNLOAD} id='action-btn' onClick={downloadHandler}>
                    <DownloadIcon />
                </span>
                <span title={MY_DOCUMENTS.EDIT} id='action-btn' onClick={editHandler}>
                    <Edit />
                </span>
                <span
                    title={MY_DOCUMENTS.MOVE_TO_BIN}
                    id='action-btn'
                    onClick={() => setIsDeleteModalOpen(true)}
                >
                    <Delete />
                </span>
            </div>
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    selectedItemId={id}
                    handleDeleteClick={deleteHandler}
                    deleteButtonName={file_name}
                    isLoading={deleteMutation.isPending}
                />
            )}
            {showEditModal && (
                <EditFileNameModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    currentFileName={file_name}
                    onSubmit={editModalOnSubmit}
                    modalTitle='Rename Document'
                    isLoading={renameMutation.isPending}
                />
            )}
        </div>
    );
};

export default MyDocumentActions;
